'use client';

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
// React 의존성 회피 위해 hook 본체가 아닌 upload-types 에서 직접 import.
import type { OnCompleteResult } from '@/shared/hooks/upload-types';
import { UploadError } from '@/shared/hooks/upload-types';
import { ProjectUploadInfo } from '@/shared/apis/asset-upload/post-presigned-url';
import { classifyUploadFailure } from '@/shared/utils/upload-error-classifier';
import { reportClassifiedUploadFailure } from '@/shared/utils/report-upload-failure';

// 동시 업로드 제한 (세마포어 패턴)
const MAX_CONCURRENT_UPLOADS = 5;
let activeUploads = 0;
const uploadQueue: (() => void)[] = [];

function acquireUploadSlot(): Promise<void> {
  if (activeUploads < MAX_CONCURRENT_UPLOADS) {
    activeUploads++;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    uploadQueue.push(() => {
      activeUploads++;
      resolve();
    });
  });
}

function releaseUploadSlot() {
  activeUploads--;
  if (uploadQueue.length > 0) {
    const next = uploadQueue.shift()!;
    next();
  }
}

export interface UploadProgress {
  fileId: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: 'pending' | 'uploading' | 'completed' | 'failed';
  statusMessage: string;
  spaceId: number;
  folderId?: number; // Multi-to-Short, Long-to-Short에서는 전달하지 않음
  createdAt: Date;
}

interface UploadState {
  uploads: UploadProgress[];
  isUploading: boolean;

  // Actions
  startUpload: (
    fileId: string,
    fileName: string,
    spaceId: number,
    folderId?: number, // Multi-to-Short, Long-to-Short에서는 전달하지 않음
  ) => void;
  updateProgress: (
    fileId: string,
    progress: number,
    statusMessage: string,
  ) => void;
  completeUpload: (fileId: string) => void;
  failUpload: (fileId: string, error: string) => void;
  removeUpload: (fileId: string) => void;
  clearCompletedUploads: () => void;
  uploadFile: ({
    id,
    file,
    spaceId,
    folderId,
    project,
    onComplete,
    uploadFn,
    duration,
    isImage,
    isAudio,
  }: {
    id?: string;
    file: File;
    spaceId: number;
    folderId?: number; // Multi-to-Short, Long-to-Short에서는 전달하지 않음
    project?: ProjectUploadInfo;
    onComplete?: (uploadData: UploadData) => void;
    uploadFn: (params: {
      spaceId?: number;
      folderId?: number;
      file: File;
      project?: ProjectUploadInfo;
      onProgress?: (fileId: string, progress: number) => void;
      onComplete?: (result: OnCompleteResult) => void;
    }) => Promise<{ success: boolean; message: string } | undefined>;
    duration?: number;
    isImage?: boolean;
    isAudio?: boolean;
  }) => Promise<void>;
}

export const useMySpaceUploadStore = create<UploadState>()(
  devtools(
    (set, get) => ({
      uploads: [],
      isUploading: false,

      startUpload: (
        fileId: string,
        fileName: string,
        spaceId: number,
        folderId?: number, // Multi-to-Short, Long-to-Short에서는 전달하지 않음
      ) => {
        // 파일 이름에서 확장자 제거하여 파일 크기 예측 (실제로는 File 객체에서 가져와야 함)
        const estimatedFileSize = fileName.length * 1024; // 임시 크기 계산

        set((state) => ({
          uploads: [
            ...state.uploads,
            {
              fileId,
              fileName,
              fileSize: estimatedFileSize,
              progress: 0,
              status: 'pending',
              statusMessage: '업로드 시작',
              spaceId,
              folderId,
              createdAt: new Date(),
            },
          ],
          isUploading: true,
        }));
      },

      updateProgress: (
        fileId: string,
        progress: number,
        statusMessage: string,
      ) => {
        set((state) => ({
          uploads: state.uploads.map((upload) =>
            upload.fileId === fileId
              ? {
                  ...upload,
                  progress,
                  statusMessage,
                  status:
                    progress === 100 ? 'completed' : ('uploading' as const),
                }
              : upload,
          ),
        }));
      },

      completeUpload: (fileId: string) => {
        set((state) => ({
          uploads: state.uploads.map((upload) =>
            upload.fileId === fileId
              ? {
                  ...upload,
                  status: 'completed' as const,
                  progress: 100,
                  statusMessage: '업로드 완료',
                }
              : upload,
          ),
          isUploading: state.uploads.some(
            (upload) =>
              upload.fileId !== fileId &&
              (upload.status === 'uploading' || upload.status === 'pending'),
          ),
        }));
      },

      failUpload: (fileId: string, error: string) => {
        set((state) => ({
          uploads: state.uploads.map((upload) =>
            upload.fileId === fileId
              ? { ...upload, status: 'failed' as const, statusMessage: error }
              : upload,
          ),
          isUploading: state.uploads.some(
            (upload) =>
              upload.fileId !== fileId &&
              (upload.status === 'uploading' || upload.status === 'pending'),
          ),
        }));
      },

      removeUpload: (fileId: string) => {
        set((state) => ({
          uploads: state.uploads.filter((upload) => upload.fileId !== fileId),
          isUploading: state.uploads.some(
            (upload) =>
              upload.fileId !== fileId &&
              (upload.status === 'uploading' || upload.status === 'pending'),
          ),
        }));
      },

      clearCompletedUploads: () => {
        set((state) => ({
          uploads: state.uploads.filter(
            (upload) =>
              upload.status !== 'completed' && upload.status !== 'failed',
          ),
        }));
      },

      uploadFile: async ({
        id,
        file,
        spaceId,
        folderId,
        project,
        onComplete,
        uploadFn,
        duration,
        isImage = false,
        isAudio = false,
      }: {
        id: string | undefined;
        file: File;
        spaceId: number;
        folderId?: number; // Multi-to-Short, Long-to-Short에서는 전달하지 않음
        project?: ProjectUploadInfo;
        onComplete?: (uploadData: UploadData) => void;
        uploadFn: (params: {
          spaceId?: number;
          folderId?: number;
          file: File;
          project?: ProjectUploadInfo;
          onProgress?: (fileId: string, progress: number) => void;
          onComplete?: (result: OnCompleteResult) => void;
        }) => Promise<{ success: boolean; message: string } | undefined>;
        duration?: number;
        isImage?: boolean;
        isAudio?: boolean;
      }) => {
        const fileId = id ?? `${file.name}-${Date.now()}-${Math.random()}`;
        const { updateProgress, completeUpload, failUpload } = get();

        // 업로드 대기열에 등록 (pending 상태)
        set((state) => ({
          uploads: [
            ...state.uploads,
            {
              fileId,
              fileName: file.name,
              fileSize: file.size,
              progress: 0,
              status: 'pending',
              statusMessage: '업로드 대기 중...',
              spaceId,
              folderId,
              createdAt: new Date(),
            },
          ],
          isUploading: true,
        }));

        // 동시 업로드 슬롯 확보 (최대 5개)
        await acquireUploadSlot();

        try {
          let fileDuration = duration;
          // 이미지가 아닌 경우 메타데이터 추출 (비디오 또는 오디오)
          if (fileDuration === undefined && !isImage) {
            const { getVideoMetadata, getAudioMetadata } =
              await import('@/shared/utils/file');

            if (isAudio) {
              const metadata = await getAudioMetadata(file);
              fileDuration = metadata.duration;
            } else {
              const metadata = await getVideoMetadata(file);
              fileDuration = metadata.duration;
            }
          }

          // useAssetUpload의 uploadFn 함수 사용 (v2.0)
          const result = await uploadFn({
            spaceId,
            folderId,
            file,
            project,
            onProgress: (_, progress) => {
              updateProgress(fileId, progress, '업로드 중...');
            },
            onComplete: (result) => {
              completeUpload(fileId);
              onComplete?.({
                id: result.id,
                preSignedUrl: result.preSignedUrl,
                thumbnailPreSignedUrl: result.thumbnailPreSignedUrl,
                duration: fileDuration || 0,
                thumbnailUploaded: result.thumbnailUploaded,
              });
            },
          });

          if (!result?.success) {
            throw new Error(result?.message || '업로드 실패');
          }
        } catch (error) {
          const errorMessage =
            error instanceof Error ? error.message : '업로드 실패';
          console.error('[Upload] 파일 업로드 실패:', {
            error,
            fileName: file.name,
            fileSize: file.size,
            fileId,
            spaceId,
            folderId,
          });
          failUpload(fileId, errorMessage);

          // 분류기 결과를 텔레메트리 errorReason 으로 전송하면 BE 가 9 reason 모두 grouping 가능.
          // 헬퍼가 mimeType / fileExtension / errorCode / httpStatus / userAgent / traceId 등 메타 자동 채움.
          const classified = classifyUploadFailure(error, file.name);
          reportClassifiedUploadFailure(error, file, classified, {
            contentId:
              error instanceof UploadError ? error.contentId : undefined,
            spaceId,
            folderId,
            uploadPath: 'my-space/uploadStore',
          });

          // 사용자 안내 토스트는 호출자가 `classifyUploadFailure` + `showUploadFailureToast` 로 처리.
          throw error;
        } finally {
          releaseUploadSlot();
        }
      },
    }),
    {
      name: 'upload-store',
    },
  ),
);

// Helper types
export interface UploadData {
  id: number;
  preSignedUrl: string;
  thumbnailPreSignedUrl?: string;
  duration: number;
  // FE 썸네일 업로드 성공 여부. false면 서버(AI media-process) 폴백 생성이 완료된 후 리스트 재조회 필요
  thumbnailUploaded?: boolean;
}
